import { useTranslation } from 'react-i18next';
import { ArrowsDownUpIcon } from '@phosphor-icons/react';
import { DropdownMenuItem } from '@/components/DropdownMenu';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuLabel,
    DropdownMenuTrigger,
} from '@/components/ui/DropdownMenu';
import {
    Tooltip,
    TooltipContent,
    TooltipTrigger,
} from '@/components/ui/Tooltip';
import { I18nNamespace } from '@/lib/i18n-namespaces';
import { cn } from '@/lib/utils';
import {
    FOLDER_SORT_OPTIONS,
    type FolderSortOption,
} from '../../hooks/useFolderSort';

interface FolderSortButtonProps {
    sortOption: FolderSortOption;
    onSortChange: (option: FolderSortOption) => void;
    open: boolean;
    onOpenChange: (open: boolean) => void;
    disabled?: boolean;
}

export function FolderSortButton({
    sortOption,
    onSortChange,
    open,
    onOpenChange,
    disabled,
}: FolderSortButtonProps) {
    const { t } = useTranslation(I18nNamespace.SOURCES);

    return (
        <DropdownMenu open={open} onOpenChange={onOpenChange}>
            <Tooltip>
                <TooltipTrigger asChild>
                    <DropdownMenuTrigger asChild>
                        <button
                            type="button"
                            aria-label={t('folders.sortFolders')}
                            disabled={disabled}
                            onClick={(event) => event.stopPropagation()}
                            data-testid="folder-sort-btn"
                            className={cn(
                                'size-4 cursor-pointer items-center justify-center rounded text-grey-200 transition-colors hover:bg-grey-500 hover:text-white-100',
                                'disabled:cursor-not-allowed disabled:opacity-60',
                                open
                                    ? 'flex'
                                    : 'hidden group-hover:flex group-focus-within:flex focus-visible:flex data-[state=open]:flex',
                            )}
                        >
                            <ArrowsDownUpIcon
                                className="size-3.5"
                                weight="bold"
                            />
                        </button>
                    </DropdownMenuTrigger>
                </TooltipTrigger>
                <TooltipContent side="top" sideOffset={4}>
                    {t('folders.sortFolders')}
                </TooltipContent>
            </Tooltip>
            <DropdownMenuContent
                variant="compact"
                align="end"
                sideOffset={4}
                className="data-[state=closed]:!animate-none"
            >
                <DropdownMenuLabel>
                    {t('folders.sortFolders')}
                </DropdownMenuLabel>
                {FOLDER_SORT_OPTIONS.map((option) => (
                    <DropdownMenuItem
                        key={option.id}
                        label={t(option.labelKey)}
                        isActive={
                            sortOption.field === option.field &&
                            sortOption.direction === option.direction
                        }
                        onClick={() =>
                            onSortChange({
                                field: option.field,
                                direction: option.direction,
                            })
                        }
                    />
                ))}
            </DropdownMenuContent>
        </DropdownMenu>
    );
}
